// Data entry template (US-01, US-03; audit 13 Oct 2026): which fields an organisation fills in for one product batch,
// on which node of the batch's chain, in which unit, and how a filled sheet is read back. Everything comes from the
// active config pack (config/fields.json, config/rules.json); the database stays the authority on what is accepted.
import type { TFunction } from 'i18next';
import type { ValueRow } from './types.ts';

export interface FieldDef {
  field_id: string;
  field_name: string;
  data_type: string;
  canonical_unit: string;
  allowed_values: string;
  source_partner: string;
  ingestible: boolean;
  acquisition_method: string;
  notes?: string;
}

interface RuleDef {
  rule_id: string;
  overlay?: string;
  data_points?: unknown;
}

export interface Org {
  org_code: string;
  name: string;
  aliases: string[];
}

export interface ChainNode {
  kind: string;
  id: string;
}

export interface TemplateRow {
  field_id: string;
  field_name: string;
  owner: string;
  node_kind: string;
  node_id: string;
  unit: string;
  allowed: string;
  current: unknown;
  current_status: string;
  list: boolean;
}

/** Mirrors public.owns_field(): one of the organisation's aliases appears in the field's owner text. */
export function ownedBy(owner: string, org: Org | null): boolean {
  if (!org) return true;
  const text = owner.toLowerCase();
  return org.aliases.some((a) => a && text.includes(a.toLowerCase()));
}

/** The dictionary entry a field id belongs to: itself, its panel (CONTAM_PANEL.Cd) or its set (WW_HM_* for WW_HM_Cd). */
export function fieldDef(fields: FieldDef[], fid: string): FieldDef | undefined {
  const base = fid.split('.')[0] ?? fid;
  return (
    fields.find((f) => f.field_id === base) ??
    fields.find((f) => f.field_id.endsWith('*') && fid.startsWith(f.field_id.slice(0, -1)))
  );
}

/** Members of panel and set fields, as the active rules read them; retired rules do not count. */
function members(fields: FieldDef[], rules: RuleDef[], values: ValueRow[]): Map<string, string[]> {
  const out = new Map<string, Set<string>>();
  const add = (fid: string) => {
    const def = fieldDef(fields, fid);
    if (!def || def.field_id === fid) return;
    out.set(def.field_id, (out.get(def.field_id) ?? new Set()).add(fid));
  };
  for (const r of rules) {
    if ((r.overlay ?? '').startsWith('retired')) continue;
    const text = JSON.stringify(r.data_points ?? []);
    for (const m of text.matchAll(/[A-Z][A-Z0-9_]*(?:\.[A-Za-z0-9]+|_[A-Za-z0-9]+)/g)) add(m[0]);
  }
  for (const v of values) add(v.field_id);
  return new Map([...out].map(([k, s]) => [k, [...s].sort()]));
}

/**
 * The template rows for one organisation (null: every organisation) and one product batch. A field goes on the node
 * of the chain where a value for it already exists, otherwise on the product batch, which the engine reads first.
 */
export function templateRows(
  fields: FieldDef[],
  rules: RuleDef[],
  org: Org | null,
  chain: ChainNode[],
  prodCode: string | null,
  values: ValueRow[],
): { rows: TemplateRow[]; notIngestible: FieldDef[] } {
  const own = fields.filter((f) => ownedBy(f.source_partner ?? '', org));
  const notIngestible = own.filter((f) => !f.ingestible || !f.acquisition_method);
  const sets = members(fields, rules, values);
  const batch = chain.find((n) => n.kind === 'product_batch');
  const rows: TemplateRow[] = [];
  for (const f of own) {
    if (!f.ingestible || !f.acquisition_method) continue;
    const ids = sets.get(f.field_id) ?? (f.field_id.includes('*') ? [] : [f.field_id]);
    for (const fid of ids) {
      const existing = values.find((v) => v.field_id === fid);
      const node = existing
        ? { kind: existing.node_kind, id: existing.node_id }
        : { kind: batch?.kind ?? 'product_batch', id: batch?.id ?? '' };
      rows.push({
        field_id: fid,
        field_name:
          fid === f.field_id
            ? f.field_name
            : `${f.field_name}: ${fid.slice(f.field_id.replace('*', '').length).replace(/^\./, '')}`,
        owner: f.source_partner,
        node_kind: node.kind,
        node_id: node.id,
        // the dictionary's unit column holds a unit for numbers and a format or value list for everything else
        unit: existing?.unit || (numeric(f.data_type) ? clean(f.canonical_unit) : ''),
        allowed: [clean(f.allowed_values), numeric(f.data_type) ? '' : clean(f.canonical_unit)]
          .filter(Boolean)
          .join(' · '),
        current: existing?.value ?? null,
        current_status: existing?.validation_status ?? '',
        list: Array.isArray(existing?.value) || /^list/i.test(f.data_type),
      });
    }
  }
  if (prodCode) for (const r of rows) if (r.node_kind === 'product') r.node_id = prodCode;
  return { rows, notIngestible };
}

const clean = (s: string | undefined) => (s && s !== '—' ? s : '');
const numeric = (dataType: string) => /^number/i.test(dataType);
// a laboratory result below or above the reporting limit is written as on the report
const CENSORED = /^\s*([<>≤≥]|not detected|n\.?\s?d\.?$|below)/i;

/** A cell as the user typed it, turned into the value the platform stores. */
export function toValue(cell: unknown, dataType: string, list: boolean): unknown {
  if (cell instanceof Date) return cell.toISOString().slice(0, 10);
  if (typeof cell === 'number' || typeof cell === 'boolean') return list ? [cell] : cell;
  const text = String(cell ?? '').trim();
  if (list)
    return text
      .split(';')
      .map((s) => s.trim())
      .filter(Boolean);
  if (/^bool/i.test(dataType)) {
    if (/^(true|yes|sí|si|evet)$/i.test(text)) return true;
    if (/^(false|no|hayır)$/i.test(text)) return false;
  }
  if (/^number/i.test(dataType) && /^-?\d+([.,]\d+)?(e-?\d+)?$/i.test(text))
    return Number(text.replace(',', '.'));
  if (/^(structured|enum \+)/i.test(dataType) && /^[[{]/.test(text)) {
    try {
      return JSON.parse(text) as unknown;
    } catch {
      return text;
    }
  }
  return text;
}

/** Column order of the "Values" sheet; reading relies on the order, not on the header text. */
export const COLUMNS = [
  'field_id',
  'field_name',
  'node_kind',
  'node_id',
  'unit',
  'allowed',
  'current',
  'current_status',
  'new_value',
  'new_unit',
  'source_document',
  'value_status',
] as const;

export interface ParsedRow {
  line: number;
  field_id: string;
  node_kind: string;
  node_id: string;
  value: unknown;
  unit: string | null;
  source_document: string;
  value_status: string;
  problems: string[];
}

/** Reads the filled "Values" sheet: rows with a new value only, each checked before anything is sent. */
export function parseSheet(
  data: unknown[][],
  fields: FieldDef[],
  rows: TemplateRow[],
  chain: ChainNode[],
  prodCode: string | null,
  t: TFunction,
): ParsedRow[] {
  const col = (name: (typeof COLUMNS)[number]) => COLUMNS.indexOf(name);
  const out: ParsedRow[] = [];
  data.slice(1).forEach((row, i) => {
    const cell = (name: (typeof COLUMNS)[number]) => row[col(name)];
    const raw = cell('new_value');
    if (raw === null || raw === undefined || String(raw).trim() === '') return;
    const field_id = String(cell('field_id') ?? '').trim();
    const node_kind = String(cell('node_kind') ?? '').trim();
    const node_id = String(cell('node_id') ?? '').trim();
    const def = fieldDef(fields, field_id);
    const problems: string[] = [];
    if (!def) problems.push(t('template.problem.unknown_field'));
    const known =
      chain.some((n) => n.kind === node_kind && n.id === node_id) ||
      (node_kind === 'product' && node_id === prodCode);
    if (!known) problems.push(t('template.problem.unknown_node'));
    const source_document = String(cell('source_document') ?? '').trim();
    if (!source_document) problems.push(t('template.problem.no_source'));
    const status = String(cell('value_status') ?? '').trim() || 'final';
    if (!['final', 'pending'].includes(status)) problems.push(t('template.problem.status'));
    const tpl = rows.find((r) => r.field_id === field_id && r.node_id === node_id);
    const dataType = def?.data_type ?? '';
    const value = toValue(raw, dataType, tpl?.list ?? /^list/i.test(dataType));
    if (/^bool/i.test(dataType) && typeof value !== 'boolean') problems.push(t('template.problem.not_bool'));
    // a single number; series, ranges and number-with-method fields are written as text
    if (/^number( set)?$/i.test(dataType) && typeof value !== 'number' && !CENSORED.test(String(value)))
      problems.push(t('template.problem.not_number'));
    const unit = String(cell('new_unit') ?? '').trim() || tpl?.unit || null;
    out.push({
      line: i + 2,
      field_id,
      node_kind,
      node_id,
      value,
      unit,
      source_document,
      value_status: status,
      problems,
    });
  });
  return out;
}

/** A database refusal in words a partner understands; the original text stays available for support. */
export function explainRefusal(message: string, owner: string | undefined, t: TFunction): string {
  if (/row-level security/i.test(message)) return t('template.refusal.not_owner', { owner: owner ?? '?' });
  if (/not in the data dictionary/i.test(message)) return t('template.problem.unknown_field');
  if (/no acquisition method/i.test(message)) return t('template.refusal.not_ingestible');
  if (/unknown .* node|unknown product/i.test(message)) return t('template.problem.unknown_node');
  return message;
}
